import db from "../../components/landing-page/services/DatabaseManager.js";
import { setVm } from "./socket/config.js";
import { setScratchBlocks, setBlocklyWorkspace } from "./blocks/index.js";
import {
    findScratchBlocks,
    getGuiInternals,
    waitForBlockly,
} from "./utils/dom.js";
import { lockRoomUI } from "./ui/room-lock.js";
import { showTopNotification } from "./ui/notification.js";
import ReplayControls from "./ui/replayWindow.js";
import ReplayEngine from "./utils/replayEngine.js";
import SessionParser from "./utils/sessionParser.js";
import { precacheProjectAssets } from "./utils/project-assets.js";

const EDITING_TARGET_TIMEOUT_MS = 10000;

export const getReplayRoomId = () =>
    new URLSearchParams(window.location.search).get("replay");

const goToLanding = () => window.location.replace(window.location.origin);

const waitForEditingTarget = (vm) =>
    new Promise((resolve) => {
        const start = Date.now();
        const check = () => {
            if (
                vm.editingTarget ||
                Date.now() - start > EDITING_TARGET_TIMEOUT_MS
            )
                resolve(vm.editingTarget);
            else setTimeout(check, 100);
        };
        check();
    });

const loadBaselineProject = async (vm, project) => {
    if (!project) return null;

    precacheProjectAssets(vm, project);
    const baselineJson = JSON.stringify(project);

    try {
        await vm.loadProject(baselineJson);
    } catch (e) {
        console.error("[Replay] Error loading baseline project:", e);
        showTopNotification(
            "No se pudo recrear el estado inicial de la sala (personajes/fondos/sonidos); se usará el proyecto por defecto.",
        );
        return null;
    }

    return baselineJson;
};

export const initReplay = async (roomId) => {
    const user = await db.waitForAuthUser();
    if (!user) {
        goToLanding();
        return;
    }

    const { vm, store } = await getGuiInternals();
    setVm(vm);

    const workspace = await waitForBlockly();
    setScratchBlocks(findScratchBlocks());
    setBlocklyWorkspace(workspace, { syncChanges: false });
    lockRoomUI(workspace, { hideToolbox: false });

    let room;
    try {
        room = await db.apiRequest(
            `/api/rooms/${encodeURIComponent(roomId)}/metrics`,
        );
    } catch (e) {
        showTopNotification(
            `No se pudo cargar la actividad de la sala: ${e.message}`,
        );
        return;
    }

    const session = new SessionParser(room.log || "");
    const baselineJson = await loadBaselineProject(
        vm,
        session.getBaselineProject(),
    );

    await waitForEditingTarget(vm);

    const controls = new ReplayControls({ onClose: goToLanding });

    store.dispatch({
        type: "projectTitle/SET_PROJECT_TITLE",
        title: `Recreación · ${room.name}`,
    });

    const timeline = session.getTimeline();
    if (timeline.length === 0) {
        showTopNotification(
            `La sala "${room.name}" no tiene actividad registrada.`,
        );
    }

    new ReplayEngine(timeline, controls, vm, baselineJson).start();
};
